import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaArrowRight, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const SLIDES = [
  {
    image: '/slider/hero-performance.png',
    eyebrow: 'LAGOM LIFE · SPORT 24/7',
    title: 'Сила начинается с состава',
    description: 'Спортивное питание и функциональные продукты для энергии, восстановления и ежедневного прогресса.',
    primary: { label: 'Перейти в каталог', to: '/catalog' },
    secondary: { label: 'Проверить качество', to: '/certificates' },
    tone: 'dark'
  },
  {
    image: '/slider/hero-goals.png',
    eyebrow: 'ПОДБОР ПО ЗАДАЧЕ',
    title: 'Ваша цель — ваш набор',
    description: 'Набор массы, энергия, восстановление или ежедневный баланс — начните с понятной подборки продуктов.',
    primary: { label: 'Подобрать продукты', to: '/catalog' },
    secondary: { label: 'Смотреть все товары', to: '/catalog' },
    tone: 'light'
  },
  {
    image: '/slider/hero-experts-v3.png',
    eyebrow: 'ЭКСПЕРТЫ LAGOM LIFE',
    title: 'Есть вопрос по составу? Спросите специалиста',
    description: 'Тренеры и нутрициологи объясняют, для чего нужен продукт, как его принимать и кому он не подойдёт.',
    primary: { label: 'Посмотреть экспертов', to: '/experts' },
    secondary: { label: 'Задать вопрос эксперту', to: '/experts' },
    tone: 'light'
  },
  {
    image: '/slider/hero-partnership-v3.png',
    eyebrow: 'ПАРТНЁРСТВО С БРЕНДОМ',
    title: 'Отправляйте ссылку. Бонус — с оплаченного заказа',
    description: 'Человек выбирает и оплачивает товар на сайте. Компания собирает и доставляет заказ. Вы видите начисление в личном кабинете.',
    primary: { label: 'Узнать о партнёрстве', to: '/referral' },
    secondary: { label: 'Условия начислений', to: '/referral' },
    tone: 'dark'
  }
];

const SliderContainer = styled.section`
  position: relative;
  width: 100%;
  overflow: hidden;
  background: #111210;
  color: #fff;
  outline: none;

  &:focus-visible {
    box-shadow: inset 0 0 0 3px #d6a84b;
  }
`;

const SliderTrack = styled.div`
  display: flex;
  width: ${props => props.$slideCount * 100}%;
  transform: translate3d(-${props => props.$currentSlide * (100 / props.$slideCount)}%, 0, 0);
  transition: transform 650ms cubic-bezier(0.22, 1, 0.36, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Slide = styled.article`
  position: relative;
  width: ${props => 100 / props.$slideCount}%;
  min-height: clamp(520px, 43vw, 660px);
  flex: 0 0 auto;
  overflow: hidden;
  background: ${props => props.$tone === 'light' ? '#f2ede4' : '#111210'};
  color: ${props => props.$tone === 'light' ? '#191a18' : '#fff'};

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: ${props => props.$tone === 'light'
      ? 'linear-gradient(90deg, rgba(247, 242, 233, 0.99) 0%, rgba(247, 242, 233, 0.94) 29%, rgba(247, 242, 233, 0.56) 48%, rgba(247, 242, 233, 0) 68%)'
      : 'linear-gradient(90deg, rgba(12, 13, 12, 0.98) 0%, rgba(12, 13, 12, 0.9) 31%, rgba(12, 13, 12, 0.46) 51%, rgba(12, 13, 12, 0) 70%)'};
    pointer-events: none;
  }

  @media (max-width: 700px) {
    min-height: 0;
    display: flex;
    flex-direction: column;

    &::after {
      display: none;
    }
  }
`;

const Artwork = styled.div`
  position: absolute;
  inset: 0;

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    object-position: center;
  }

  @media (max-width: 700px) {
    position: relative;
    inset: auto;
    height: 210px;
    flex: 0 0 210px;
    background: #161715;

    img {
      object-position: 68% center;
    }
  }
`;

const Content = styled.div`
  position: relative;
  z-index: 2;
  min-height: clamp(520px, 43vw, 660px);
  max-width: 1280px;
  margin: 0 auto;
  padding: 72px 92px 86px;
  display: flex;
  align-items: center;

  @media (max-width: 980px) {
    padding-left: 64px;
    padding-right: 64px;
  }

  @media (max-width: 700px) {
    min-height: 0;
    width: 100%;
    margin: 0;
    padding: 28px 22px 76px;
    align-items: flex-start;
    background: ${props => props.$tone === 'light'
      ? 'linear-gradient(145deg, #f8f4ec 0%, #ece4d7 100%)'
      : 'linear-gradient(145deg, #191b19 0%, #0d0e0d 100%)'};
  }
`;

const ContentInner = styled.div`
  max-width: 600px;
`;

const Eyebrow = styled.p`
  margin: 0 0 18px;
  color: ${props => props.$tone === 'light' ? '#8b641f' : '#e2b45a'};
  font-size: 13px;
  line-height: 1.2;
  font-weight: 800;
  letter-spacing: 0.18em;
`;

const Title = styled.h1`
  max-width: 590px;
  margin: 0;
  font-family: var(--font-heading, 'BebasNeuePro', 'Arial Black', sans-serif);
  font-size: clamp(52px, 5.2vw, 78px);
  line-height: 0.94;
  letter-spacing: 0.015em;
  font-weight: 700;
  text-transform: uppercase;

  @media (max-width: 700px) {
    font-size: clamp(39px, 12vw, 53px);
    line-height: 0.98;
  }
`;

const Description = styled.p`
  max-width: 560px;
  margin: 24px 0 0;
  color: ${props => props.$tone === 'light' ? '#4d4b45' : 'rgba(255, 255, 255, 0.76)'};
  font-size: clamp(16px, 1.35vw, 19px);
  line-height: 1.55;

  @media (max-width: 700px) {
    margin-top: 18px;
    font-size: 15px;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 30px;

  @media (max-width: 520px) {
    display: grid;
    grid-template-columns: 1fr;
    margin-top: 24px;
  }
`;

const ActionLink = styled(Link)`
  min-height: 50px;
  padding: 0 22px;
  border: 1px solid ${props => props.$primary ? '#d7a94b' : (props.$tone === 'light' ? 'rgba(25, 26, 24, 0.25)' : 'rgba(255, 255, 255, 0.34)')};
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: ${props => props.$primary ? '#d7a94b' : 'transparent'};
  color: ${props => props.$primary ? '#141512' : 'inherit'};
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.035em;
  transition: transform 180ms ease, background 180ms ease, border-color 180ms ease;

  &:hover {
    transform: translateY(-2px);
    background: ${props => props.$primary ? '#ebbd61' : (props.$tone === 'light' ? 'rgba(25, 26, 24, 0.08)' : 'rgba(255, 255, 255, 0.1)')};
  }

  &:focus-visible {
    outline: 3px solid ${props => props.$tone === 'light' ? '#191a18' : '#fff'};
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const SliderButton = styled.button`
  position: absolute;
  z-index: 5;
  top: 50%;
  ${props => props.$side}: 24px;
  width: 46px;
  height: 46px;
  transform: translateY(-50%);
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: rgba(14, 15, 14, 0.55);
  color: #fff;
  backdrop-filter: blur(10px);
  transition: background 180ms ease, transform 180ms ease;

  &:hover {
    background: #d7a94b;
    color: #151613;
    transform: translateY(-50%) scale(1.05);
  }

  &:focus-visible {
    outline: 3px solid #fff;
    outline-offset: 3px;
  }

  @media (max-width: 700px) {
    top: auto;
    bottom: 18px;
    ${props => props.$side}: 18px;
    width: 42px;
    height: 42px;
    transform: none;

    &:hover {
      transform: scale(1.04);
    }
  }
`;

const SliderDots = styled.div`
  position: absolute;
  z-index: 5;
  left: 50%;
  bottom: 29px;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 700px) {
    bottom: 34px;
  }
`;

const Dot = styled.button`
  width: ${props => props.$active ? '34px' : '9px'};
  height: 9px;
  padding: 0;
  border: 0;
  border-radius: 99px;
  background: ${props => props.$active ? '#d7a94b' : 'rgba(255, 255, 255, 0.52)'};
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.16);
  transition: width 220ms ease, background 220ms ease;

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 4px;
  }
`;

const Slider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const touchStartX = useRef(null);

  const nextSlide = () => setCurrentSlide(prev => (prev + 1) % SLIDES.length);
  const prevSlide = () => setCurrentSlide(prev => (prev - 1 + SLIDES.length) % SLIDES.length);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (isPaused || prefersReducedMotion) return undefined;

    const intervalId = window.setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % SLIDES.length);
    }, 6500);

    return () => window.clearInterval(intervalId);
  }, [isPaused]);

  const handleKeyDown = event => {
    if (event.key === 'ArrowLeft') prevSlide();
    if (event.key === 'ArrowRight') nextSlide();
  };

  const handleTouchStart = event => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = event => {
    if (touchStartX.current === null) return;
    const distance = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(distance) > 45) {
      distance > 0 ? prevSlide() : nextSlide();
    }
    touchStartX.current = null;
  };

  return (
    <SliderContainer
      aria-label="Главные предложения Lagom Life"
      aria-roledescription="карусель"
      tabIndex="0"
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget)) setIsPaused(false);
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <SliderTrack $currentSlide={currentSlide} $slideCount={SLIDES.length}>
        {SLIDES.map((slide, index) => (
          <Slide
            key={slide.image}
            $slideCount={SLIDES.length}
            $tone={slide.tone}
            aria-hidden={index !== currentSlide}
            aria-label={`${index + 1} из ${SLIDES.length}`}
          >
            <Artwork>
              <img
                src={slide.image}
                alt=""
                loading="eager"
                fetchPriority={index === 0 ? 'high' : 'auto'}
              />
            </Artwork>
            <Content $tone={slide.tone}>
              <ContentInner>
                <Eyebrow $tone={slide.tone}>{slide.eyebrow}</Eyebrow>
                <Title>{slide.title}</Title>
                <Description $tone={slide.tone}>{slide.description}</Description>
                <Actions>
                  <ActionLink
                    to={slide.primary.to}
                    $primary
                    $tone={slide.tone}
                    tabIndex={index === currentSlide ? 0 : -1}
                  >
                    {slide.primary.label} <FaArrowRight aria-hidden="true" />
                  </ActionLink>
                  <ActionLink
                    to={slide.secondary.to}
                    $tone={slide.tone}
                    tabIndex={index === currentSlide ? 0 : -1}
                  >
                    {slide.secondary.label}
                  </ActionLink>
                </Actions>
              </ContentInner>
            </Content>
          </Slide>
        ))}
      </SliderTrack>

      <SliderButton type="button" $side="left" onClick={prevSlide} aria-label="Предыдущий баннер">
        <FaChevronLeft aria-hidden="true" />
      </SliderButton>
      <SliderButton type="button" $side="right" onClick={nextSlide} aria-label="Следующий баннер">
        <FaChevronRight aria-hidden="true" />
      </SliderButton>

      <SliderDots aria-label="Выбор баннера">
        {SLIDES.map((slide, index) => (
          <Dot
            key={slide.image}
            type="button"
            $active={index === currentSlide}
            onClick={() => setCurrentSlide(index)}
            aria-label={`Показать баннер ${index + 1}`}
            aria-current={index === currentSlide ? 'true' : undefined}
          />
        ))}
      </SliderDots>
    </SliderContainer>
  );
};

export default Slider;
